// What the customer's landing page needs so their personalisation holds, once Test Connection
// passes (onboarding step 4 and Settings → Setup).
//
// Self-serve onboarding, Phase 3 (spec §8.4, §9 in Cloudflare-Worker,
// https://github.com/Digital-Mozarts/Cloudflare-Worker, branch feature/self-serve-onboarding): the
// api-worker proposes SPA extensions from the page's scan and Test Connection's diagnostics
// (POST /api/onboarding/sites/recommendation). The panel says what it recommends and why; the
// account owner or an admin approves it; a page with a hard flag waits for our team; what is live
// can be switched off.
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const EXTENSION_NAMES = {
    'spa-reapply': 'Keep re-applying your changes',
    'spa-route-lifecycle': 'Handle route changes',
};
const ERROR = 'Something went wrong. Please try again.';
// Before a test passes the address can still be changing (the customer is typing it).
const SAVED_STATE_DELAY_MS = 400;
const messageOf = (err, fallback) => (err && err.response && err.response.data && err.response.data.message) || fallback;
const nameOf = (id) => EXTENSION_NAMES[id] || id;

const SiteExtensionsPanel = ({ app_url, landingPage, testPassed }) => {
    const [site, setSite] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // Before a test passes: what the page already has, and what staff decided about it (their note,
    // or a proposal they handed back to approve). A new test would replace both, so this is the only
    // place the customer sees them.
    useEffect(() => {
        if (!landingPage || testPassed) return undefined;
        let cancelled = false;
        const timer = setTimeout(() => {
            axios.get(`${app_url}/api/onboarding/sites`, { params: { landingPage }, withCredentials: true })
                .then((response) => { if (!cancelled) setSite((response.data && response.data.site) || null); })
                .catch(() => {});
        }, SAVED_STATE_DELAY_MS);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [app_url, landingPage, testPassed]);

    // Once Test Connection passes: what the page needs, from its scan and this test.
    useEffect(() => {
        if (!landingPage || !testPassed) return undefined;
        let cancelled = false;
        setBusy(true);
        setError('');
        axios.post(`${app_url}/api/onboarding/sites/recommendation`, { landingPage }, { withCredentials: true })
            .then((response) => { if (!cancelled) setSite(response.data.site); })
            .catch((err) => { if (!cancelled) setError(messageOf(err, "We couldn't analyse this page just now.")); })
            .finally(() => { if (!cancelled) setBusy(false); });
        return () => { cancelled = true; };
    }, [app_url, landingPage, testPassed]);

    const act = async (action, body) => {
        setBusy(true);
        setError('');
        try {
            const response = await axios.post(`${app_url}/api/onboarding/sites/${site.id}/${action}`, body, { withCredentials: true });
            setSite(response.data.site);
        } catch (err) {
            setError(messageOf(err, ERROR));
        } finally {
            setBusy(false);
        }
    };

    const proposal = site && site.proposal;
    const open = site && (site.status === 'proposed' || site.status === 'needs_staff');
    // A page that has only been checked, or has no record yet, has nothing to show.
    const hasContent = !!site && ((open && proposal) || site.live || site.status === 'off' || site.status === 'failed');
    if (!hasContent && !busy && !error) return null;
    const hardFlags = proposal ? proposal.flags.filter((f) => f.level === 'hard') : [];
    const softFlags = proposal ? proposal.flags.filter((f) => f.level === 'soft') : [];
    const evidenceFor = (about) => ((proposal && proposal.evidence.find((e) => e.about === about)) || {}).text;

    return (
        <section aria-label="Recommended for your page" style={{ marginTop: 24 }}>
            <h4 style={titleStyle}>Recommended for your page</h4>
            {busy && !site && <p className="text-muted" aria-live="polite">Checking what your page needs…</p>}

            {site && site.live && (
                <p className="text-success mb-2">
                    Live on your page: {site.live.extensions.length ? site.live.extensions.map(nameOf).join(', ') : 'nothing extra'}.
                </p>
            )}
            {site && site.status === 'off' && <p className="text-muted mb-2">Switched off. Your page runs without these extras.</p>}
            {site && site.status === 'failed' && (
                <p className="text-danger mb-2">We couldn't switch this on. Our team has been told and will finish it.</p>
            )}

            {open && proposal && (
                <>
                    {proposal.extensions.length === 0 ? (
                        <p>{evidenceFor('none') || 'Your page needs nothing extra.'}</p>
                    ) : (
                        <ul style={{ paddingLeft: 18 }}>
                            {proposal.extensions.map((extension) => (
                                <li key={extension.id} className="mb-2">
                                    <strong>{nameOf(extension.id)}</strong>
                                    <br />
                                    <span className="text-muted">{evidenceFor(extension.id)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {hardFlags.length > 0 && !site.canApprove && (
                        <div className="mb-2">
                            <p className="mb-1" style={{ color: '#B54708' }}>Our team will review this page first.</p>
                            <ul style={{ paddingLeft: 18 }}>{hardFlags.map((flag) => <li key={flag.id}>{flag.text}</li>)}</ul>
                        </div>
                    )}
                    {site.staffNote && <p className="text-danger">{site.staffNote}</p>}
                    {softFlags.map((flag) => <p key={flag.id} className="text-muted mb-1">{flag.text}</p>)}
                    {site.canApprove ? (
                        <button type="button" className="btn btn-primary btn-sm mt-2" disabled={busy} onClick={() => act('approve', { proposalId: proposal.id })}>
                            Approve
                        </button>
                    ) : (
                        site.status === 'proposed' && <p className="text-muted">Ask your account owner or an admin to approve this.</p>
                    )}
                </>
            )}

            {site && site.canDisable && (
                <button type="button" className="btn btn-outline-secondary btn-sm mt-2" disabled={busy} onClick={() => act('disable', {})}>
                    Switch off
                </button>
            )}
            {error && <p className="text-danger mt-2" aria-live="polite">{error}</p>}
        </section>
    );
};

const titleStyle = { fontSize: 16, fontWeight: 600, color: '#101828', marginBottom: 8 };

export default SiteExtensionsPanel;
